/**
 * Table Component
 *
 * Reusable data table component with sorting, pagination, and styling.
 * Displays tabular data in a clean, accessible format.
 *
 * @component
 */

import React, { useState } from 'react';
import './Table.css';

/**
 * Table component
 *
 * @param {Object} props
 * @param {Array} props.columns - Table columns configuration
 * @param {Array} props.data - Table data
 * @param {boolean} props.sortable - Enable sorting
 * @param {boolean} props.hoverable - Enable row hover
 * @param {boolean} props.striped - Enable striped rows
 * @param {Function} props.onRowClick - Row click handler
 * @param {string} props.emptyMessage - Message when no data
 * @param {boolean} props.loading - Loading state
 * @param {string} props.className - Additional CSS classes
 */
const Table = ({
  columns = [],
  data = [],
  sortable = true,
  hoverable = true,
  striped = true,
  onRowClick,
  emptyMessage = 'No hay datos disponibles',
  loading = false,
  className = '',
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  /**
   * Handle column sort
   */
  const handleSort = (key) => {
    if (!sortable) return;

    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  /**
   * Sort data based on current sort config
   */
  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal === bVal) return 0;

      const comparison = aVal > bVal ? 1 : -1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, sortConfig]);

  const tableClasses = [
    'table',
    hoverable && 'table-hoverable',
    striped && 'table-striped',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (loading) {
    return (
      <div className="table-loading">
        <div className="spinner-large"></div>
        <p>Cargando datos...</p>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="table-empty">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="table-container">
      <table className={tableClasses}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={sortable && column.sortable !== false ? 'sortable' : ''}
                onClick={() =>
                  sortable && column.sortable !== false && handleSort(column.key)
                }
                style={{ width: column.width }}
              >
                <div className="th-content">
                  <span>{column.label}</span>
                  {sortable && column.sortable !== false && sortConfig.key === column.key && (
                    <span className="sort-icon">
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, rowIndex) => (
            <tr
              key={row.id || rowIndex}
              onClick={() => onRowClick?.(row)}
              className={onRowClick ? 'clickable' : ''}
            >
              {columns.map((column) => (
                <td key={column.key}>
                  {column.render
                    ? column.render(row[column.key], row)
                    : row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
